import dagre from 'dagre';
import {
	EdgeMarker,
	MarkerType,
	Position,
} from 'reactflow';
import {
	Dependency,
	TaskExecutionData,
} from 'src/@types/selfServeTypes/taskExecutionData.interface';
import {
	GraphLayoutDirection,
	NodeType,
} from 'src/constants/selfServe/executionViewConstants';

const nodeWidth = 200;
const nodeHeight = 50;

export const getLayoutedElements = (
	nodes,
	edges,
	direction = GraphLayoutDirection.TopToBottom
) => {
	const dagreGraph = new dagre.graphlib.Graph();

	dagreGraph.setDefaultEdgeLabel(() => ({}));

	const isHorizontal = direction === GraphLayoutDirection.LeftToRight;
	const graphSettings = {
		rankdir: direction,
		acyclicer: 'greedy',
		nodesep: 40,
		ranksep: 60,
		ranker: 'tight-tree',
	};
	dagreGraph.setGraph(graphSettings);

	nodes.forEach((node) => {
		dagreGraph.setNode(node.id, { width: nodeWidth, height: nodeHeight });
	});

	edges.forEach((edge) => {
		dagreGraph.setEdge(edge.source, edge.target);
	});

	dagre.layout(dagreGraph);

	nodes.forEach((node) => {
		const nodeWithPosition = dagreGraph.node(node.id);

		node.targetPosition = isHorizontal ? Position.Left : Position.Top;
		node.sourcePosition = isHorizontal ? Position.Right : Position.Bottom;

		// We are shifting the dagre node position (anchor=center center) to the top left
		// so it matches the React Flow node anchor point (top left).
		node.position = {
			x: nodeWithPosition.x - nodeWidth / 2,
			y: nodeWithPosition.y - nodeHeight / 2,
		};
		node.data.alignment = direction;
	});

	return { nodes, edges };
};

export const generateGraphForDisplay = (
	taskExecutions: TaskExecutionData[],
	dependencies: Dependency[]
) => {
	const startNode = generateTerminalNode(NodeType.START);
	const endNode = generateTerminalNode(NodeType.END);
	let initialNodes = taskExecutions.map(generateTaskNode);
	const initialEdges = dependencies.map(generateEdge);

	initialNodes = [...initialNodes, startNode, endNode];
	const { nodes: layoutedNodes, edges: layoutedEdges } = getLayoutedElements(
		initialNodes,
		initialEdges
	);

	return { layoutedNodes, layoutedEdges };
};

const generateTaskNode = (taskExecutionData) => {
	return {
		id: taskExecutionData.taskId,
		type: 'taskNode',
		data: { ...taskExecutionData, alignment: GraphLayoutDirection.TopToBottom },
	};
};

const generateTerminalNode = (nodeType) => {
	return {
		id: nodeType,
		type: 'terminalNode',
		data: { type: nodeType, alignment: GraphLayoutDirection.TopToBottom },
	};
};

const generateEdge = (dependency: Dependency) => {
	return {
		id: `${dependency.supporting}-${dependency.dependent}`,
		source: dependency.supporting,
		target: dependency.dependent,
		type: 'default',
		style: { stroke: '#000' },
		markerEnd: ArrowEnd,
	};
};

const ArrowEnd: EdgeMarker = {
	type: MarkerType.ArrowClosed,
	color: '#000',
	width: 12,
	height: 12,
};
